import PropTypes from 'prop-types'
import { Checkbox } from '@/components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatETString } from '@/utils/date-format-util'
import { transformAmount } from '@/utils/string-format-utils'
import { useIsMobile } from '@/hooks/use-is-mobile'
import Spinner from '@/assets/icons/spinner'

const PlaidTransactionPickerContent = ({
  transactions,
  selectedTransactionIds,
  onCheckboxChange,
  isLoading,
  error,
}) => {
  const isMobile = useIsMobile()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <Spinner size="40" stroke="#98a2b3" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <p className="text-sm text-error-500">Failed to load transactions. Please try again.</p>
      </div>
    )
  }

  // Handle case where transactions might be undefined or not an array
  const transactionsArray = Array.isArray(transactions) ? transactions : []

  if (transactionsArray.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <p className="text-sm text-neutral-700">No transactions found.</p>
        {transactions !== undefined && !Array.isArray(transactions) && (
          <p className="text-xs text-neutral-500 mt-2">
            Unexpected response format: {typeof transactions}
          </p>
        )}
      </div>
    )
  }

  if (isMobile) {
    return (
      <div className="flex flex-col px-6 pb-6">
        {/* Header Row */}
        <div className="w-full py-2 border-b border-gray-200 flex items-center">
          <div className="flex-1">
            <span className="text-[12px] font-normal text-neutral-700">Merchant and Date</span>
          </div>
          <div className="ml-3">
            <span className="text-[12px] font-normal text-neutral-700">Amount</span>
          </div>
        </div>
        {transactionsArray.map(transaction => {
          const isSelected = selectedTransactionIds.includes(transaction.transaction_id)
          const amount = Math.abs(transaction.amount || 0)
          const merchantName = transaction.merchant_name || transaction.name || 'Unknown'

          return (
            <div
              key={transaction.transaction_id}
              className={`w-full flex flex-col transition-all duration-300 border-b border-gray-100 ${
                isSelected ? 'bg-gray-50' : ''
              }`}
            >
              <div className="flex items-center py-2 px-2">
                {/* Checkbox */}
                <div className="flex items-center justify-center h-full pr-3">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={checked => onCheckboxChange(transaction.transaction_id, checked)}
                    aria-label={`Select ${merchantName}`}
                    className="w-4 h-4"
                  />
                </div>
                {/* Merchant Name and Date */}
                <div className="flex flex-col min-w-0 max-w-1/2 flex-1">
                  <span className="text-[14px] font-normal text-neutral-900 truncate" title={merchantName}>
                    {merchantName}
                  </span>
                  <span className="text-[14px] font-light text-neutral-700 mt-0.5">
                    {formatETString(transaction.date, 'MMM dd, yyyy') || '-'}
                  </span>
                </div>
                {/* Amount */}
                <div className="flex items-center ml-3 text-right">
                  <span className="text-[14px] font-semibold text-neutral-700 whitespace-nowrap">
                    {transformAmount(amount * 100)}
                  </span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // Desktop view
  return (
    <div className="w-full overflow-x-hidden px-4 md:px-6 flex-shrink-0">
      <Table className="w-full border-collapse">
        <TableHeader>
          <TableRow>
            <TableHead className="text-[12px] font-normal text-neutral-700 w-[180px] py-2">Date</TableHead>
            <TableHead className="w-[180px] text-[12px] font-normal text-neutral-700 py-2">Merchant Name</TableHead>
            <TableHead className="w-[200px] text-[12px] font-normal text-neutral-700 py-2">Description</TableHead>
            <TableHead className="text-[12px] font-normal text-neutral-700 w-[120px] py-2">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {transactionsArray.map(transaction => {
            const isSelected = selectedTransactionIds.includes(transaction.transaction_id)
            const amount = Math.abs(transaction.amount || 0)
            const merchantName = transaction.merchant_name || transaction.name || 'Unknown'
            const description = transaction.name || transaction.merchant_name || ''

            return (
              <TableRow
                key={transaction.transaction_id}
                className={`${isSelected ? 'bg-[#F7F7F7]' : ''}`}
              >
                <TableCell className="text-[12px] font-light text-neutral-700 w-[180px] h-12 py-2">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={checked => onCheckboxChange(transaction.transaction_id, checked)}
                      className="w-4 h-4"
                    />
                    <span>{formatETString(transaction.date, 'MMM dd, yyyy') || '-'}</span>
                  </div>
                </TableCell>
                <TableCell className="text-[14px] font-normal text-neutral-900 w-[180px] max-w-[180px] truncate h-12 py-2">
                  {merchantName}
                </TableCell>
                <TableCell className="text-[14px] font-light text-neutral-900 w-[200px] max-w-[200px] truncate h-12 py-2">
                  {description}
                </TableCell>
                <TableCell className="text-[14px] font-semibold text-neutral-700 w-[120px] h-12 py-2">
                  {transformAmount(amount * 100)}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

PlaidTransactionPickerContent.propTypes = {
  transactions: PropTypes.array.isRequired,
  selectedTransactionIds: PropTypes.array.isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.object,
}

export default PlaidTransactionPickerContent

